/**
 * QtyStepperInput — number input with +/- buttons for quantity selection.
 *
 * Used in partial fulfillment item picker. 56px touch targets per UX spec.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Minus, Plus } from "lucide-react";

interface QtyStepperInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max: number;
  disabled?: boolean;
}

export function QtyStepperInput({
  value,
  onChange,
  min = 1,
  max,
  disabled = false,
}: QtyStepperInputProps) {
  const [inputValue, setInputValue] = useState(String(value));

  const clamp = (v: number) => Math.min(Math.max(v, min), max);

  const handleDecrement = () => {
    const next = clamp(value - 1);
    if (next !== value) {
      onChange(next);
      setInputValue(String(next));
    }
  };

  const handleIncrement = () => {
    const next = clamp(value + 1);
    if (next !== value) {
      onChange(next);
      setInputValue(String(next));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed)) {
      setInputValue(String(value));
      return;
    }
    const clamped = clamp(parsed);
    onChange(clamped);
    setInputValue(String(clamped));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 min-w-[48px] min-h-[48px] shrink-0"
        onClick={handleDecrement}
        disabled={disabled || value <= min}
        aria-label="Decrease quantity"
      >
        <Minus className="h-3.5 w-3.5" />
      </Button>
      <input
        type="text"
        inputMode="numeric"
        className="h-8 w-10 rounded-md border border-input bg-background text-center text-sm
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1
          disabled:cursor-not-allowed disabled:opacity-50"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label="Quantity to ship"
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 min-w-[48px] min-h-[48px] shrink-0"
        onClick={handleIncrement}
        disabled={disabled || value >= max}
        aria-label="Increase quantity"
      >
        <Plus className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
